import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Moon, Eye, EyeOff, Loader2, Ear, Pause } from "lucide-react";
import axios from "axios";
import { AUTH } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PREVIEW_VOICES = [
  { id: "shimmer", name: "Luna" },
  { id: "onyx", name: "Barnaby" },
  { id: "nova", name: "Aria" },
  { id: "fable", name: "Jasper" },
];

const backdrop = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const modal = {
  hidden: { opacity: 0, scale: 0.92, y: 30 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 25 } },
  exit: { opacity: 0, scale: 0.92, y: 20 },
};

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(". ");
  return String(detail);
}

export default function AuthModal({ open, onClose, onAuth, initialMode = "register" }) {
  const [mode, setMode] = useState(initialMode);

  // Sync mode when modal opens
  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewVoiceId, setPreviewVoiceId] = useState(null);
  const [previewAudioId, setPreviewAudioId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewPlayingId, setPreviewPlayingId] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const previewAudioRef = useRef(null);

  useEffect(() => {
    if (!open) previewAudioRef.current?.pause();
  }, [open]);

  useEffect(() => {
    if (previewAudioId && previewAudioRef.current) {
      previewAudioRef.current.play().catch(() => {});
    }
  }, [previewAudioId]);

  const handlePreview = async (voiceId) => {
    if (previewPlayingId === voiceId) {
      previewAudioRef.current?.pause();
      return;
    }
    if (previewVoiceId === voiceId && previewAudioId) {
      const audio = previewAudioRef.current;
      if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
      return;
    }
    setPreviewLoadingId(voiceId);
    setPreviewError("");
    try {
      const { data } = await axios.get(`${API}/voices/preview/${voiceId}`, { withCredentials: true });
      setPreviewVoiceId(voiceId);
      setPreviewAudioId(data.audio_id);
    } catch {
      setPreviewError("Couldn't load that sample — try again.");
      setTimeout(() => setPreviewError(""), 3000);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const reset = () => {
    setName(""); setEmail(""); setPassword(""); setError(""); setShowPw(false);
  };

  const toggle = () => {
    setMode((m) => (m === "register" ? "login" : "register"));
    reset();
  };

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? { name, email, password } : { email, password };
      const { data } = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      onAuth(data);
      onClose();
      reset();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={AUTH.modal}
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          variants={backdrop}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={(e) => e.target === e.currentTarget && onClose()}
          style={{ background: "rgba(11,9,26,0.85)", backdropFilter: "blur(12px)" }}
        >
          <motion.div
            variants={modal}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="w-full max-w-md glass-card rounded-3xl p-8 relative my-8 sm:my-0"
          >
            {/* Close */}
            <button
              data-testid={AUTH.closeButton}
              onClick={onClose}
              className="absolute top-2 right-2 p-3 rounded-full text-slate-400 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/30">
                <Moon size={20} className="text-white" />
              </div>
              <h2 className="font-serif text-2xl font-bold text-white mb-1">
                {mode === "register" ? "Begin Your Journey" : "Welcome Back"}
              </h2>
              <p className="text-sm text-slate-400">
                {mode === "register"
                  ? "Create your free account and generate your first magical story"
                  : "Sign in to continue weaving bedtime magic"}
              </p>
            </div>

            {/* Google Sign-In */}
            <button
              type="button"
              data-testid={AUTH.googleButton}
              onClick={handleGoogleAuth}
              className="w-full mb-5 py-3 rounded-xl bg-white text-[#1F1A3D] font-semibold text-sm hover:bg-slate-100 transition-colors flex items-center justify-center gap-2.5 shadow-sm"
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917z"/>
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l6.19 5.238C41.436 35.316 44 30.138 44 24c0-1.341-.138-2.651-.389-3.917z"/>
              </svg>
              Continue with Google
            </button>
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1 h-px bg-purple-500/15" />
              <span className="text-[11px] text-slate-500 uppercase tracking-wider">or continue with email</span>
              <div className="flex-1 h-px bg-purple-500/15" />
            </div>

            {/* Form */}
            <form data-testid={AUTH.form} onSubmit={handleSubmit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">Your Name</label>
                  <input
                    data-testid={AUTH.nameInput}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/20 transition-all"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">Email</label>
                <input
                  data-testid={AUTH.emailInput}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/20 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5 tracking-wider uppercase">Password</label>
                <div className="relative">
                  <input
                    data-testid={AUTH.passwordInput}
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="w-full px-4 py-3 pr-11 rounded-xl bg-white/5 border border-purple-500/20 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/20 transition-all"
                  />
                  <button
                    type="button"
                    data-testid="auth-password-toggle"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 min-h-[40px] min-w-[40px] flex items-center justify-center text-slate-500 hover:text-slate-300"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                data-testid={AUTH.submitButton}
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 size={16} className="animate-spin" /> {mode === "register" ? "Creating account..." : "Signing in..."}</>
                ) : (
                  mode === "register" ? "Create Free Account" : "Sign In"
                )}
              </button>
            </form>

            {mode === "register" && (
              <div className="mt-5 pt-5 border-t border-purple-500/10">
                <p className="text-xs text-slate-500 mb-2.5 flex items-center gap-1.5">
                  <Ear size={12} className="text-amber-400" /> Not sure which narrator? Preview a voice
                </p>
                <div className="flex flex-wrap gap-2">
                  {PREVIEW_VOICES.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`auth-voice-preview-${v.id}`}
                      onClick={() => handlePreview(v.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        previewPlayingId === v.id
                          ? "border-amber-400/50 bg-amber-500/10 text-amber-300"
                          : "border-purple-500/20 text-slate-400 hover:text-white hover:border-purple-500/40"
                      }`}
                    >
                      {previewLoadingId === v.id ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : previewPlayingId === v.id ? (
                        <Pause size={10} />
                      ) : (
                        <Ear size={11} />
                      )}
                      {v.name}
                    </button>
                  ))}
                </div>
                {previewError && (
                  <p data-testid="auth-voice-preview-error" className="text-xs text-red-400 mt-2">{previewError}</p>
                )}
                {previewAudioId && (
                  <audio
                    ref={previewAudioRef}
                    src={`${API}/audio/${previewAudioId}`}
                    onPlay={() => setPreviewPlayingId(previewVoiceId)}
                    onPause={() => setPreviewPlayingId(null)}
                    onEnded={() => setPreviewPlayingId(null)}
                  />
                )}
              </div>
            )}

            {/* Toggle */}
            <p className="mt-5 text-center text-sm text-slate-500">
              {mode === "register" ? "Already have an account? " : "Don't have an account? "}
              <button
                data-testid={AUTH.toggleLink}
                onClick={toggle}
                className="text-amber-400 hover:text-amber-300 font-medium transition-colors"
              >
                {mode === "register" ? "Sign in" : "Create one free"}
              </button>
            </p>

            {mode === "register" && (
              <p className="mt-3 text-center text-xs text-slate-600">
                Free account includes 1 complete personalized bedtime story
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
